import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ShopInsuranceContent,
  type PaymentOption,
  type ShopInsuranceMode,
} from '@/features/shop-insurance';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useIsGenworthMode } from '@/features/genworth/hooks/use-genworth-mode';
import { EdjPilotQaABlocked } from '@/shared/components/edj-pilot-qaa-blocked';

export function ShopInsurancePage({ clientId }: ClientIdProps) {
  const isEdjPilot = useIsEdjPilotAdvisor();
  const isGenworthMode = useIsGenworthMode();

  if (isEdjPilot) {
    return <EdjPilotQaABlocked clientId={clientId} />;
  }

  // Genworth drops the shop/apply-for-a-policy surface entirely.
  if (isGenworthMode) {
    return (
      <Navigate
        to={`/clients/${clientId}/onboarding/funding-sources`}
        replace
      />
    );
  }

  return <ShopInsurancePageContent clientId={clientId} />;
}

function ShopInsurancePageContent({ clientId }: ClientIdProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100vh';
    body.style.height = '100vh';
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.height = '';
      body.style.height = '';
    };
  }, []);

  const [searchParams] = useSearchParams();

  const queryOptions = useMemo(() => {
    // The 'couples' tab was removed; map any legacy ?selectTabOption=couples to Single.
    const rawTab = searchParams.get('selectTabOption') ?? undefined;
    return {
      paymentOption: (searchParams.get('paymentOption')?.replace('_', ' ') ?? undefined) as
        | PaymentOption
        | undefined,
      buyerOption: (searchParams.get('buyerOption') ?? undefined) as
        | '1-buyer'
        | '2-buyer'
        | undefined,
      selectTabOption: (rawTab === 'couples' ? 'single' : rawTab) as ShopInsuranceTab | undefined,
      mode: (searchParams.get('mode') ?? undefined) as ShopInsuranceMode | undefined,
    };
  }, [searchParams]);

  const handleClose = useCallback(() => {
    navigate(`/clients/${clientId}/onboarding/funding-sources`);
  }, [navigate, clientId]);

  return (
    <div className="bg-background flex h-screen flex-col overflow-hidden">
      <ShopInsuranceContent
        onClose={handleClose}
        queryOptions={queryOptions}
      />
    </div>
  );
}
